import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../../core/services/core.translate.service';
import { TrackerFilterService } from '../../services/tracker.filter.service';
import { TrackerQueryService } from '../../services/tracker.query.service';
import * as _ from 'lodash';

@Component({
  selector: 'ad-hoc-filters',
  template: require('./adhoc.filters.component.html'),
})
export class AdHocFiltersComponent implements OnInit {
  @Input() tracker;
  @Input() filters;
  @Input() sortFields;

  @Output() onUpdate: EventEmitter<any> = new EventEmitter<any>();
  @Output() onReset: EventEmitter<any> = new EventEmitter<any>();

  title: string;
  sortString: string;

  constructor(
    private stateService: StateService,
    private translationService: GfTranslateService,
    private trackerFilterService: TrackerFilterService,
    private trackerQueryService: TrackerQueryService
  ) {}

  ngOnInit() {
    this.title = this.translationService.translate('Filter Criteria');

    this.sortFields = {
      'id': 'ID',
      'tracker_id': this.translationService.translate('Tracker'),
      'summary': this.translationService.translate('Summary'),
      'priority': this.translationService.translate('Priority'),
      'lastModifiedDate': this.translationService.translate('Last Modified'),
      'submittedByName': this.translationService.translate('Submitter'),
      'openDate': this.translationService.translate('Open Date'),
      'closeDate': this.translationService.translate('Close Date')
    };

    this.sortString = this.getSortString();
  }

    /**
     * Given a string, renders it into a title for the UI
     * @param {string}
     * @returns {string}
     */
  renderTitle(str: string): string {
    return _.split(str, '_').map(_.capitalize).join(' ');
  }

  checkSubmit(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.applyFilters();
    }
  }

    /**
     * Given a filter, resets the value to the default
     * @param {object}
     * @returns {object}
     */
  resetFilterField(filter: any): any {
    if ([4, 6, 9, 10, 11, 20, 22, 24].includes(parseInt(filter.fieldType))) {
      filter.value = '';
    } else {
      filter.value = {
        id: '',
        value: '',
        label: 'Any'
      };
    }

    this.applyFilters();

    return filter;
  }

    /**
     * Given a filter, returns true if this field should show a plain filter badge
     * @param {object}
     * @returns {boolean}
     */
  showPlainFilterBadge(filter: any): boolean {
    return [4, 6, 10, 11, 20, 22, 24].includes(filter.fieldType) && filter.value != '';
  }

    /**
     * Given a filter, returns true if this field should show a select filter badge
     * @param {object}
     * @returns {boolean}
     */
  showSelectFilterBadge(filter: any): boolean {
    return [1, 2, 3, 5, 7, 8, 12, 25].includes(parseInt(filter.fieldType)) &&
      !_.isEmpty(filter.value) && filter.value.id != '' &&
      _.has(filter.value, 'label') && !(filter.alias == 'assignees' && _.get(filter, 'value.id', 0) == 100);
  }

    /**
     * Given a value range filter, return true if the upper/lower value is set
     */
  showRangeFilterBadge(filter: any, rangeLimit: string): boolean {
    if ([9].includes(parseInt(filter.fieldType)) && !_.isEmpty(filter.value)) {
      const arrRanges = filter.value.split('|-|');
      if (arrRanges.length > 1) {
        return ((rangeLimit === 'from') ? arrRanges[0] : arrRanges[1]) != '';
      }
    }
    return false;
  }

  isSortedBy(field: string, direction: string): boolean {
    return this.trackerFilterService.isSortedBy(field, direction);
  }

  setSort(key: string, direction: string) {
    const params = {
      sort_field: key,
      sort_dir: direction
    };
    this.stateService.go('.', params)
    .then(() => {
        this.sortString = this.getSortString();
        this.applyFilters();
    });
  }

  resetFilters() {
    this.filters = this.trackerFilterService.getFilters(this.tracker);

    const params = {
      unixName: this.stateService.params.unixName,
      tracker: this.stateService.params.tracker,
      query: this.stateService.params.query,
      page_num: 1,
      sort_field: 'id',
      sort_dir: 'desc',
      filters: null
    };

    this.trackerQueryService.saveCustomQueryParams(this.tracker.id, params)
      .catch(() => {
        // Ignore errors saving custom query params
      });
    this.stateService.go('.', params, { inherit: false, notify: true, reload: false })
    .then(() => {
        this.sortString = this.getSortString();
        this.onReset.emit(params);
    });
  }

  /**
   * Processes and applies the filter values to the state
   */
  applyFilters() {
    const filterValues = this.trackerFilterService
      .getFilterValues(this.filters)
      .reduce((carry, filter) => _.extend(carry, filter), {});

    const sortParams = _.pick(this.stateService.params, ['sort_field', 'sort_dir']);
    const params = {
      page_num: 1,
      ...sortParams,
      filters: _.isEmpty(filterValues) ? null : encodeURIComponent(JSON.stringify(filterValues))
    };

    this.stateService.go('.', params, { notify: true, reload: false })
      .then(() => {
        // Saves filter values to preferences
        const saveParams = {
          unixName: this.stateService.params.unixName,
          tracker: this.stateService.params.tracker,
          query: this.stateService.params.query,
          page_num: 1,
          ...sortParams,
          ...filterValues
        };
        this.trackerQueryService.saveCustomQueryParams(this.tracker.id, saveParams)
          .catch(() => {
            // Ignore errors saving custom query params
          });
        this.onUpdate.emit(saveParams);
      });
  }

  /**
   * Given a value range filter, reset the lower limit value
   */
  resetFilterFieldRangeFrom(filter: any): any {
    this.resetRangeValueItem(filter, 0);
    this.applyFilters();
    return filter;
  }

  resetFilterFieldRangeTo(filter: any): any {
    this.resetRangeValueItem(filter, 1);
    this.applyFilters();
    return filter;
  }

  /**
   * Given a range value encoded string, return the start or end value, or empty string
   */
  getValueFromRange(rangeValue: string, rangeItem: string): string {
    const arrValues = rangeValue.split('|-|');
    let index = -1;

    switch (rangeItem) {
      case 'from':
        index = 0;
        break;
      case 'to':
        index = 1;
        break;
    }

    return (index >= 0 && arrValues.length > 1 && arrValues[index] !== '') ? arrValues[index] : '';
  }

  private getSortString(): string {
    const queryString = this.stateService.params;
    const field = _.get(queryString, 'sort_field', 'id');
    const dir = _.get(queryString, 'sort_dir', 'asc');

    return this.sortFields[field] || field + ' ' + dir.toUpperCase();
  }

  /**
   * Given a value range filter, reset the corresponding upper/lower limit
   */
  private resetRangeValueItem(filter: any, index: number) {
    const arrValues = filter.value.split('|-|');
    if (arrValues.length > 0) {
      arrValues[index] = '';
      filter.value = arrValues.join('|-|');
    }
  }

  /**
   * Cleans the params object by removing properties that corresponde to filters
   * with an empty value (considering values of type string, dates or objects)
   *
   * @param filters - The filters object containing main_filters and extra_filters.
   * @param params - The params object to be cleaned.
   * @returns The cleaned params object.
   */
  private cleanParams(
    filters: {
      main_filters: Array<{ alias: string; value: any }>;
      extra_filters: Array<{ alias: string; value: any }>;
    },
    params: { [key: string]: any }
  ): { [key: string]: any } {

  /**
   * Determines if a filter value meets the criteria for removal.
   *
   * @param filterValue - The value from the filter object.
   * @returns True if the filter value indicates the param should be removed, else false.
   */
  const shouldRemove = (filterValue: any): boolean => {
    if (typeof filterValue === 'string') {
      return filterValue === '' || filterValue === '|-|';
    } else if (
      typeof filterValue === 'object' &&
      filterValue !== null &&
      'value' in filterValue &&
      typeof filterValue.value === 'string'
    ) {
      return filterValue.value === '';
    }
    return false;
  };

  // Extract all aliases from main_filters and extra_filters where shouldRemove is true
  const aliasesToRemove = [
    ...new Set(
      Object.values(filters)
        .flat()
        .filter((filter: { alias: string; value: any }) => shouldRemove(filter.value))
        .map((filter) => filter.alias)
    ),
  ];

    return _.omit(params, aliasesToRemove);
  }

  //Wrapper to the corresponding service method
  isTypeText(item) {
    return this.trackerFilterService.isTypeText(item);
  }
          
  //Wrapper to the corresponding service method
  isTypeSelect(item) {
    return this.trackerFilterService.isTypeSelect(item);
  }
  
  // Wrapper to the corresponding service method
  isTypeDate(item) { 
    return this.trackerFilterService.isTypeDate(item);
  }
}
